import { useEffect, useRef, useState } from "react";

import ChatbotService from "../../../services/chatbot.service";

const ActionButton = ({ onAction, children, ...props }) => {
  const buttonRef = useRef(null);
  const actionRef = useRef(onAction);

  useEffect(() => {
    actionRef.current = onAction;
  }, [onAction]);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return undefined;

    const handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      actionRef.current?.();
    };

    button.addEventListener("click", handleClick);
    return () => button.removeEventListener("click", handleClick);
  }, []);

  return (
    <button ref={buttonRef} type="button" {...props}>
      {children}
    </button>
  );
};

const GuideContentAI = ({
  title = "",
  value = "",
  onChange,
  onActiveChange,
  promptId = "guide-content-ai-prompt",
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [loadingMode, setLoadingMode] = useState("");
  const [error, setError] = useState("");
  const promptRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const abortControllerRef = useRef(null);

  const normalizedTitle = String(title || "");
  const normalizedValue = String(value || "");
  const isActive = showPrompt || !!loadingMode || !!suggestion;

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (
      suggestion &&
      (title !== suggestion.requestedTitle ||
        (value !== suggestion.requestedContent && value !== suggestion.content))
    ) {
      setSuggestion(null);
    }
  }, [suggestion, title, value]);

  useEffect(() => {
    if (showPrompt) {
      const promptElement = promptRef.current;
      if (!promptElement) return undefined;

      const handlePromptInput = (event) => {
        setPrompt(event.target.value);
        setError("");
      };

      promptElement.addEventListener("input", handlePromptInput);
      promptElement.addEventListener("change", handlePromptInput);
      promptElement.focus();
      promptElement.scrollIntoView?.({ block: "nearest" });

      return () => {
        promptElement.removeEventListener("input", handlePromptInput);
        promptElement.removeEventListener("change", handlePromptInput);
      };
    }

    return undefined;
  }, [showPrompt]);

  const requestContent = async (mode) => {
    const currentPrompt = promptRef.current?.value ?? prompt;
    if (loadingMode) return;
    if (mode === "create" && !currentPrompt.trim()) {
      setError("Describe what the block should tell guests.");
      return;
    }
    if (mode === "optimize" && !normalizedValue.trim()) {
      setError("Add some block text before optimizing it.");
      return;
    }

    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoadingMode(mode);
    setError("");
    setSuggestion(null);
    if (mode === "optimize") setShowPrompt(false);

    try {
      const response = await ChatbotService.guideContent(
        {
          mode,
          title: normalizedTitle.trim(),
          prompt: mode === "create" ? currentPrompt.trim() : undefined,
          content: mode === "optimize" ? normalizedValue : undefined,
        },
        { signal: abortController.signal },
      );

      if (requestSequenceRef.current !== requestId) return;

      const suggestedContent = response?.data?.suggested_content;
      if (!suggestedContent) {
        throw new Error("Rue AI returned an empty suggestion.");
      }

      setSuggestion({
        content: suggestedContent,
        requestedContent: normalizedValue,
        requestedTitle: normalizedTitle,
      });
      setShowPrompt(false);
    } catch (requestError) {
      if (
        requestSequenceRef.current !== requestId ||
        requestError?.code === "ERR_CANCELED"
      ) {
        return;
      }

      console.error("Error generating guidebook content:", requestError);
      setError(
        requestError?.response?.data?.error ||
          "We couldn't create a suggestion. Please try again.",
      );
    } finally {
      if (requestSequenceRef.current === requestId) {
        abortControllerRef.current = null;
        setLoadingMode("");
      }
    }
  };

  const useSuggestion = () => {
    onChange(suggestion.content);
    setSuggestion(null);
    setPrompt("");
  };

  const cancelAiTask = () => {
    requestSequenceRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setShowPrompt(false);
    setPrompt("");
    setSuggestion(null);
    setLoadingMode("");
    setError("");
  };

  const openCreatePrompt = () => {
    if (showPrompt) {
      cancelAiTask();
      return;
    }

    setShowPrompt(true);
    setSuggestion(null);
    setError("");
  };

  return (
    <div className="guide-content-ai">
      {!isActive && (
        <div className="uk-flex uk-flex-middle uk-flex-wrap guide-content-ai-actions">
          <ActionButton
            className="uk-button uk-button-default uk-button-small sms-ai-optimize-button"
            onAction={openCreatePrompt}
            aria-expanded={false}
            aria-controls={promptId}
          >
            Create with Rue AI
          </ActionButton>
          <ActionButton
            className="uk-button uk-button-default uk-button-small sms-ai-optimize-button"
            onAction={() => requestContent("optimize")}
          >
            Optimize with Rue AI
          </ActionButton>
        </div>
      )}

      {loadingMode === "optimize" && (
        <div className="uk-width-1-1">
          <span data-uk-spinner="ratio: 0.45"></span>
          <ActionButton
            className="uk-button hms-btn hms-white-btn uk-button-small"
            onAction={cancelAiTask}
          >
            Cancel
          </ActionButton>
        </div>
      )}

      {showPrompt && (
        <div className="uk-width-1-1 uk-margin-small-top">
          <label className="uk-form-label" htmlFor={promptId}>
            What should this block tell guests?
          </label>
          <textarea
            ref={promptRef}
            id={promptId}
            className="uk-textarea"
            rows="3"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Describe the accurate details Rue AI should include."
          />
          <ActionButton
            className="uk-button hms-btn hms-red-btn uk-button-small uk-margin-small-top"
            onAction={() => requestContent("create")}
            disabled={loadingMode === "create" || !prompt.trim()}
          >
            {loadingMode === "create" ? "Creating…" : "Create content"}
          </ActionButton>
          <ActionButton
            className="uk-button hms-btn hms-white-btn uk-button-small uk-margin-small-top"
            onAction={cancelAiTask}
          >
            Cancel
          </ActionButton>
        </div>
      )}

      {suggestion && (
        <div className="uk-width-1-1 uk-alert-primary uk-margin-small-top">
          <strong>Rue AI suggestion</strong>
          <div
            className="ai-assistant-response uk-margin-small-top uk-text-break"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {suggestion.content}
          </div>
          <div className="uk-margin-small-top">
            <ActionButton
              className="uk-button hms-btn hms-red-btn uk-button-small"
              onAction={useSuggestion}
            >
              Use suggestion
            </ActionButton>
            <ActionButton
              className="uk-button hms-btn hms-white-btn uk-button-small"
              onAction={() => setSuggestion(null)}
            >
              Dismiss
            </ActionButton>
          </div>
          <p className="hms-small-note uk-margin-small-top uk-margin-remove-bottom">
            Review AI-generated content before saving, especially property-specific details.
          </p>
        </div>
      )}

      {error && (
        <div className="uk-width-1-1 uk-alert-danger uk-margin-small-top">
          {error}
        </div>
      )}
    </div>
  );
};

export default GuideContentAI;
